import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, User, Phone, Lock, AlertCircle, Loader2 } from 'lucide-react';
import { formatPhoneNumber, validatePhoneNumber } from '@/lib/masks';
import { toast } from 'sonner';
import ConfirmationModal from '@/components/ConfirmationModal';
import MaintenanceBanner from '@/components/MaintenanceBanner';

// Sistema temporariamente indisponível
const MAINTENANCE_MODE = true;

interface FormData {
  email: string;
  nome: string;
  telefone: string;
  profissao: string;
  senha: string;
  confirmacaoSenha: string;
}

interface FormErrors {
  email?: string;
  nome?: string;
  telefone?: string;
  profissao?: string;
  senha?: string;
  confirmacaoSenha?: string;
}

const Signup = () => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    nome: '',
    telefone: '',
    profissao: 'Corretor',
    senha: '',
    confirmacaoSenha: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { signUp, user } = useAuth();
  const navigate = useNavigate();

  // Redireciona para a página inicial se já estiver logado
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'telefone') {
      setFormData({
        ...formData,
        telefone: formatPhoneNumber(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Limpa o erro do campo quando ele é alterado
    if (errors[name as keyof FormErrors]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    
    // Validação do email
    if (!formData.email) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    // Validação do nome
    if (!formData.nome) {
      newErrors.nome = 'Nome é obrigatório';
    }
    
    // Validação do telefone
    if (formData.telefone && !validatePhoneNumber(formData.telefone)) {
      newErrors.telefone = 'Telefone inválido';
    }
    
    // Validação da profissão
    if (!formData.profissao) {
      newErrors.profissao = 'Profissão é obrigatória';
    }
    
    // Validação da senha
    if (!formData.senha) {
      newErrors.senha = 'Senha é obrigatória';
    } else if (formData.senha.length < 8) {
      newErrors.senha = 'A senha deve ter pelo menos 8 caracteres';
    }
    
    // Validação da confirmação de senha
    if (formData.senha !== formData.confirmacaoSenha) {
      newErrors.confirmacaoSenha = 'As senhas não correspondem';
    }
    
    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Valida todo o formulário
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error, emailDeliveryError } = await signUp(
        formData.email,
        formData.senha,
        {
          nome: formData.nome,
          telefone: formData.telefone,
          profissao: formData.profissao
        }
      );
      
      if (error) {
        if (error.message?.includes('already')) {
          toast.error('Este email já está em uso');
          setErrors(prev => ({ ...prev, email: 'Este email já está em uso' }));
        } else {
          toast.error('Erro ao criar conta');
        }
      } else {
        if (emailDeliveryError) {
          toast.warning('Conta criada, mas pode haver um problema com a entrega do email de confirmação. Entre em contato com o suporte se não receber o email.');
        }
        setShowConfirmModal(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
    navigate('/login');
  };

  return (
    <div className="container mx-auto flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] py-12 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-blue-dark">Criar Conta</h1>
          <p className="mt-2 text-gray-600">Preencha os campos para se cadastrar</p>
          <p className="mt-2 text-gray-600">O Broker Base está em Fase de Desenvolvimento, portanto todas as funcionalidades, planos e condições de acesso ao sistema podem ser revogadas a qualquer momento, sem aviso prévio.</p>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
          {MAINTENANCE_MODE && (
            <MaintenanceBanner variant="card" className="mb-6" />
          )}
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email*
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={MAINTENANCE_MODE}
                  className={`pl-10 ${errors.email ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.email}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="nome" className="block text-sm font-medium text-gray-700">
                Nome*
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="nome"
                  name="nome"
                  type="text"
                  placeholder="Seu nome completo"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  disabled={MAINTENANCE_MODE}
                  className={`pl-10 ${errors.nome ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
              </div>
              {errors.nome && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.nome}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="telefone" className="block text-sm font-medium text-gray-700">
                Telefone
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="telefone"
                  name="telefone"
                  type="text"
                  placeholder="(11) 99999-9999"
                  value={formData.telefone}
                  onChange={handleChange}
                  disabled={MAINTENANCE_MODE}
                  maxLength={15}
                  className={`pl-10 ${errors.telefone ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
              </div>
              {errors.telefone && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.telefone}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="profissao" className="block text-sm font-medium text-gray-700">
                Profissão*
              </label>
              <select
                id="profissao"
                name="profissao"
                value={formData.profissao}
                onChange={handleChange}
                required
                disabled={MAINTENANCE_MODE}
                className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${errors.profissao ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
              >
                <option value="Corretor">Corretor</option>
                <option value="Imobiliária">Imobiliária</option>
                <option value="Incorporador">Incorporador</option>
                <option value="Investidor">Investidor</option>
                <option value="Outros">Outros</option>
              </select>
              {errors.profissao && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.profissao}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="senha" className="block text-sm font-medium text-gray-700">
                Senha*
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="senha"
                  name="senha"
                  type="password"
                  placeholder="Mínimo 8 caracteres"
                  value={formData.senha}
                  onChange={handleChange}
                  required
                  disabled={MAINTENANCE_MODE}
                  className={`pl-10 ${errors.senha ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
              </div>
              {errors.senha && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.senha}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmacaoSenha" className="block text-sm font-medium text-gray-700">
                Confirmar Senha*
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="confirmacaoSenha"
                  name="confirmacaoSenha"
                  type="password"
                  placeholder="Confirme sua senha"
                  value={formData.confirmacaoSenha}
                  onChange={handleChange}
                  required
                  disabled={MAINTENANCE_MODE}
                  className={`pl-10 ${errors.confirmacaoSenha ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
              </div>
              {errors.confirmacaoSenha && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {errors.confirmacaoSenha}
                </p>
              )}
            </div>

            <Button 
              type="submit" 
              className={`w-full ${MAINTENANCE_MODE ? 'bg-gray-400 cursor-not-allowed' : ''}`}
              disabled={isSubmitting || MAINTENANCE_MODE}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Criando conta...
                </>
              ) : MAINTENANCE_MODE ? (
                'Sistema Indisponível'
              ) : (
                'Criar Conta'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Já tem uma conta?{' '}
              <Link to="/login" className="font-medium text-blue-dark hover:underline">
                Faça login
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      <ConfirmationModal 
        isOpen={showConfirmModal} 
        onClose={handleCloseModal} 
        email={formData.email}
      />
    </div>
  );
};

export default Signup;
